import { onOpenAuthPopup } from "components/popups/PopupDispatcher";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { apiMain } from "services";
import { IAppState } from "store/app-state";
import { metrics } from "utils/metrics";
import { showNotification } from "utils/notification";

export const useFollowing = () => {
  const currentUser = useSelector((state: IAppState) => state.user.currentUser?.username);
  const [following, setFollowing] = useState<string[]>([]);

  const sendFollow = (id: string, context: string | null) => {
    showNotification({
      title: `Started following ${id}`,
      variant: "success"
    });

    apiMain.followUser(id, context).then(() => {
      console.debug(`Successfully subscribed to ${id}.`);
    }).then(() => {
      metrics.sendFollowUserClicked({ userName: id });
    }).catch((e) => {
      showNotification({
        title: `Error subscribing to ${id}: ${e.message}.`,
        variant: "error"
      });
      console.error(`Error subscribing to ${id}: ${e.message}.`);
    });
  };

  const sendUnfollow = (id: string) => {
    showNotification({
      title: `Stopped following ${id}`,
      variant: "success"
    });

    apiMain.unfollowUser(id).then(() => {
      console.debug(`Successfully unsubscribed from ${id}.`);
    }).catch((e) => {
      showNotification({
        title: `Error unsubscribing from ${id}: ${e.message}.`,
        variant: "success"
      });
      console.error(`Error unsubscribing from ${id}: ${e.message}.`);
    });
  };

  const follow = useCallback((followId: string, context: string | null) => {
    setFollowing(prev => {
      sendFollow(followId, context);
      const without = prev.filter((userName: string): boolean => userName !== followId);
      return [...without, followId];
    });
  }, []);

  const unfollow = useCallback((unfollowId: string) => {
    sendUnfollow(unfollowId);
    setFollowing(prev => prev.filter((userName: string): boolean => userName !== unfollowId));
  }, []);

  const toggleSubscription = useCallback((userName: string) => {
    if (!currentUser) {
      onOpenAuthPopup();
      return;
    }

    const isFollowing = following.indexOf(userName) >= 0;

    setFollowing((prev: string[]) => {
      if (isFollowing) {
        return prev.filter((id: string) => id !== userName);
      } else {
        return [...prev, userName];
      }
    });

    if (isFollowing) {
      sendUnfollow(userName);
    } else {
      sendFollow(userName, null);
    }
  }, [following]);

  useEffect(() => {
    apiMain.getFollowingIds().then((ids: string[]) => {
      setFollowing(ids);
      console.debug(`Subscription list read, ${ids.length} items.`);
    }).catch((e) => {
      console.error(`ERROR reading subscription list: ${e.message}.`);
    });
  }, []);

  return {
    following,
    follow,
    unfollow,
    toggleSubscription,
  };
};
